/**
 * Hook over `GET /api/admin/timesheet-audit` (task 17 §3.10).
 *
 * Keyset-paged через `useInfiniteQuery`. Backend выдаёт ≤100 строк за
 * раз; курсор «<iso_created_at>|<id>» собирается из последней записи
 * предыдущей страницы. Пока последняя страница полная — есть `next`.
 *
 * Список workers — отдельный hook (мелкий справочник, не пагинируется).
 */
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { ApiError } from "@/api/client";
import {
  cursorFromEntry,
  listTimesheetAudit,
  listTimesheetWorkers,
  type AdminTimesheetWorker,
  type ListTimesheetAuditParams,
  type TimesheetAuditEntry,
} from "@/api/adminTimesheetAudit";

export type TimesheetAuditFilters = Omit<ListTimesheetAuditParams, "cursor" | "limit">;

const PAGE_SIZE = 100;

export const TIMESHEET_AUDIT_KEY = (filters: TimesheetAuditFilters) =>
  ["timesheet-audit", filters] as const;

export function useTimesheetAudit(filters: TimesheetAuditFilters, enabled = true) {
  return useInfiniteQuery<TimesheetAuditEntry[], Error>({
    queryKey: TIMESHEET_AUDIT_KEY(filters),
    queryFn: ({ pageParam }) =>
      listTimesheetAudit({
        ...filters,
        cursor: typeof pageParam === "string" ? pageParam : undefined,
        limit: PAGE_SIZE,
      }),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => {
      if (lastPage.length < PAGE_SIZE) return undefined;
      const tail = lastPage[lastPage.length - 1];
      return tail ? cursorFromEntry(tail) : undefined;
    },
    retry: (failureCount, error) => {
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        return false;
      }
      return failureCount < 1;
    },
    staleTime: 30_000,
    enabled,
  });
}

export function useTimesheetAuditWorkers(enabled = true) {
  return useQuery<AdminTimesheetWorker[], Error>({
    queryKey: ["timesheet-audit-workers"],
    queryFn: listTimesheetWorkers,
    staleTime: 60_000,
    enabled,
  });
}
